import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Router } from '@angular/router';
import { ProjectCommunicationService } from '../../../communication/project-comms.service';
import {
  ProjectValidation, ValidationItem,
  ValidationSectionName
} from '../../../models/data-structures/project-validation';
import { ProjectPropName } from '../../../models/project/project.model';
import { GhgEmissionsPropName } from '../../../models/project/ghg-emission/ghg-emissions.model';
import { FinancialPropName } from '../../../models/project/financial/financial.model';
import { ProjectFinancialService } from '../../financial/project-financial.service';
import { FeedstockService } from '../../feedstock/feedstock.service';
import { PlantSpecificationsPropName } from '../../../models/project/plant-specifications/plant-specifications.model';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import { UserpilotReloader } from '../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-progress-popup-dialog',
  templateUrl: './progress-popup.component.html',
  styleUrls: [
    './progress-popup.component.scss'
  ],
  providers: [FeedstockService]
})
export class ProgressPopupDialogComponent extends UserpilotReloader implements OnInit {
  private basePath = [];
  private expandedNodes = [];
  sectionName: string;
  treeControl = new FlatTreeControl<FlatItemNode>(
    node => node.level, node => node.expandable);

  @Input() dataIn: {
    projectValidation: ProjectValidation,
    section: ValidationSectionName,
    onlyShowInvalid: boolean,
    disableConfirmClose: boolean,
  };

  private transformer = (node: ItemNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      valid: node.valid,
      path: node.path,
      errMsgs: node.errMsgs,
    };
  }
  // tslint:disable-next-line:member-ordering
  treeFlattener = new MatTreeFlattener(
    this.transformer, node => node.level, node => node.expandable, node => node.children);
  // tslint:disable-next-line:member-ordering
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  // tslint:disable-next-line:member-ordering
  sectionTree: Object | Array<Object>;
  // tslint:disable-next-line:member-ordering
  treeData: ItemNode[];

  constructor(private projectCommService: ProjectCommunicationService,
              private projectFinService: ProjectFinancialService,
              private feedstockService: FeedstockService,
              private dialogRef: MatDialogRef<ProgressPopupDialogComponent>,
              private router: Router,
              public translationService: AnessaTranslationService,
              @Inject(MAT_DIALOG_DATA) dataIn: {
                projectValidation: ProjectValidation,
                section: ValidationSectionName,
                onlyShowInvalid: boolean,
                disableConfirmClose: boolean,
              }
  ) {
    super();
    this.dataIn = dataIn;
  }

  get ValidationSectionName() {
    return ValidationSectionName;
  }

  hasChild = (_: number, node: FlatItemNode) => node.expandable;

  getFormattedErrMsgs(node: ItemNode) {
    let formattedString = '';
    if (node) {
      if (Array.isArray(node.errMsgs)) {
        for (const msg of node.errMsgs) {
          formattedString += this.translationService.get(msg) + '\n';
        }
      }
    }
    return formattedString;
  }

  expandNode(node: FlatItemNode) {
    if (!node.valid && !this.expandedNodes.includes(node)) {
      this.treeControl.expand(node);
      this.expandedNodes.push(node);
    }
  }

  close() {
    this.dialogRef.close();
  }

  ngOnInit() {
    this.determineSection();
    this.treeData = this.getChildren(this.sectionTree, this.basePath);
    if (this.treeData.length > 0) {
      this.dataSource.data = this.treeData;
    } else {
      this.dataSource.data = this.determineEmptyMessage(this.basePath[this.basePath.length - 1]);
    }
  }

  determineEmptyMessage(name: string) {
    if (name === ProjectPropName.feedstock) {
      return [{
        name: this.translationService.get('PROGRESS.FEED_REQ'),
        path: ['project', ProjectPropName.feedstock],
      }];
    } else {
      return [];
    }
  }

  determineSection() {
    switch (this.dataIn.section) {
      case ValidationSectionName.ProjectInfo:
        this.basePath = ['project', ProjectPropName.projectInfo];
        this.sectionName = this.translationService.get('PROGRESS.PROJ_INFO');
        break;
      case ValidationSectionName.Feedstock:
        this.basePath = ['project', ProjectPropName.feedstock];
        this.sectionName = this.translationService.get('PROGRESS.FEED');
        break;
      case ValidationSectionName.PlantSpecifications:
        this.basePath = ['project', ProjectPropName.plantSpecifications];
        this.sectionName = this.translationService.get('PROGRESS.PLANT_SPEC');
        break;
      case ValidationSectionName.Financial:
        this.basePath = ['project', ProjectPropName.financial];
        this.sectionName = this.translationService.get('PROGRESS.FINANCIAL');
        break;
      case ValidationSectionName.GhgEmissions:
        this.basePath = ['project', ProjectPropName.ghgEmissions];
        this.sectionName = this.translationService.get('PROGRESS.GHG');
        break;
      case ValidationSectionName.All:
        this.basePath = ['project'];
        this.sectionTree = this.dataIn.projectValidation.tree;
        if (this.dataIn.onlyShowInvalid) {
          this.sectionName = this.translationService.get('PROGRESS.PROJ_PROGRESS');
        } else {
          this.sectionName = this.translationService.get('PROGRESS.OVERALL');
        }
        return;
    }
    this.sectionTree = this.dataIn.projectValidation.tree[this.dataIn.section];
  }

  navigateToLocation(path: string[]) {
    // Disable navigation on project input page
    if (this.projectCommService.isProjectInput()) {
      return;
    }
    if (path.length < 1) {
      throw Error('invalid path');
    }
    if (path[0] === 'project') {
      this.getSidebarNavigation(path);
    } else {
      throw Error('path has to start with project');
    }

  }

  routeToPath(path: string[]) {
    let url = '';
    for (const item of path) {
      url += '/' + item;
    }
    this.router.navigateByUrl(url);
  }

  private getSidebarNavigation(path: string[]) {
    if (path.length === 1) {
      this.routeToPath(path);
    } else {
      if (path.length < 2) {
        throw Error('invalid path');
      }
      switch (path[1]) {
        case ProjectPropName.projectInfo:
          this.routeToPath([path[0], 'projectinfo']);
          this.close();
          break;
        case ProjectPropName.feedstock:
          this.getMaterialLocation(path);
          break;
        case ProjectPropName.plantSpecifications:
          this.getPlantSpecificationsLocation(path);
          break;
        case ProjectPropName.financial:
          this.getFinancialLocation(path);
          break;
        case ProjectPropName.ghgEmissions:
          this.getGhgLocation(path);
      }
    }
  }

  private getMaterialLocation(path: string[]) {
    if (path[0] === 'project') {
      if (path[1] === ProjectPropName.feedstock) {
        if (path[2]) {
          const matIndex = this.feedstockService.getFeedstockByName(path[2]);
          this.router.navigateByUrl(path[0] + '/feedstock/edit/' + matIndex);

        } else {
          this.router.navigateByUrl(path[0] + '/feedstock');
        }
      }
    }
    this.close();
  }

  private getPlantSpecificationsLocation(path: string[]) {
    if (path[0] === 'project') {
      if (path[1] === ProjectPropName.plantSpecifications) {
        this.router.navigateByUrl(path[0] + '/plant-specifications');
      }
    }
    this.close();
  }

  private getFinancialLocation(path: string[]) {
    if (path[0] === 'project') {
      if (path[1] === ProjectPropName.financial) {
        if (path[2]) {
          switch (path[2]) {
            case FinancialPropName.feedstock:
              this.getFinFeedstockLocation(path);
              break;
            case PlantSpecificationsPropName.generatedProducts:
              this.getFinGenProdLocation(path);
              break;
            case FinancialPropName.income:
              this.getFinIncomeLocation(path);
              break;
            case FinancialPropName.capex:
              this.getFinCapexLocation(path);
              break;
            case FinancialPropName.opex:
              this.getFinOpexLocation(path);
              break;
            case FinancialPropName.consumedFuels:
              this.getFinConFuelLocation(path);
              break;
            case FinancialPropName.investment:
              this.getFinInvestmentLocation(path);
              break;
            case FinancialPropName.additional_products:
              this.getFinAdditionalProductsLocation(path);
              break;
            default:
              this.router.navigateByUrl(path[0]);
          }
        } else {
          this.router.navigateByUrl(path[0]);
        }
      }
    }
    this.close();
  }

  private getFinFeedstockLocation(path: string[]) {
    if (path[2] === FinancialPropName.feedstock && path[3]) {
      const matIndex = this.feedstockService.getFeedstockByName(path[3]);
      this.router.navigate([path[0], 'financial', 'feedstock', 'edit', matIndex]);
      this.close();
      return;
    }
    this.router.navigate([path[0], 'financial', 'feedstock']);
    this.close();
  }

  private getFinGenProdLocation(path: string[]) {
    const finGenProdUrl = 'generated-product';
    if (path[2] === PlantSpecificationsPropName.generatedProducts) {
      if (path[3]) {
        const genProdIndex = this.projectCommService.getGeneratedProductIndexByType(path[3]);
        this.router.navigate([path[0], 'financial', finGenProdUrl, 'edit', genProdIndex]);
        this.close();
        return;
      }
      this.router.navigate([path[0], 'financial', finGenProdUrl]);
      this.close();
      return;
    }
  }

  private getFinIncomeLocation(path: string[]) {
    if (path[2] === FinancialPropName.income) {
      const incomeUrl = 'income';
      if (path[3]) {
        const incomeIndex = this.projectFinService.getIncomeIndexByName(path[3]);
        this.router.navigate([path[0], 'financial', incomeUrl, 'edit', incomeIndex]);
        this.close();
        return;
      }
      this.router.navigate([path[0], 'financial', incomeUrl]);
      this.close();
      return;
    }
  }

  private getFinCapexLocation(path: string[]) {
    const capexUrl = 'capex';
    if (path[2] === FinancialPropName.capex) {
      if (path[3]) {
        const capexIndex = this.projectFinService.getCapexIndexByName(path[3]);
        this.router.navigate([path[0], 'financial', capexUrl, 'edit', capexIndex]);
        this.close();
        return;
      }
      this.router.navigate([path[0], 'financial', capexUrl]);
      this.close();
      return;
    }
  }

  private getFinOpexLocation(path: string[]) {
    const opexUrl = 'opex';
    if (path[2] === FinancialPropName.opex) {
      if (path[3]) {
        const opexIndex = this.projectFinService.getOpexIndexByName(path[3]);
        this.router.navigate([path[0], 'financial', opexUrl, 'edit', opexIndex]);
        this.close();
        return;
      }
      this.router.navigate([path[0], 'financial', opexUrl]);
      this.close();
      return;
    }
  }

  private getFinConFuelLocation(path: string[]) {
    const finConFuelUrl = 'consumed-fuel';
    if (path[2] === FinancialPropName.consumedFuels) {
      if (path[3]) {
        const conFuelIndex = this.projectCommService.getConsumedFuelIndexByType(path[3]);
        this.router.navigate([path[0], 'financial', finConFuelUrl, 'edit', conFuelIndex]);
        this.close();
        return;
      }
      this.router.navigate([path[0], 'financial', finConFuelUrl]);
      this.close();
      return;
    }
  }

  private getFinInvestmentLocation(path: string[]) {
    this.router.navigateByUrl(path[0] + '/financial/investment');
    this.close();
  }

  private getFinAdditionalProductsLocation(path: string[]) {
    const finAdditionalProductUrl = 'additional-product';
    if (path[2] === FinancialPropName.additional_products) {
      if (path[3]) {
        const additionalProductsIndex = this.projectCommService.getAdditionalProductsIndexByType(path[3]);
        this.router.navigate([path[0], 'financial', finAdditionalProductUrl, 'edit', additionalProductsIndex]);
        this.close();
        return;
      }
      this.router.navigate([path[0], 'financial', finAdditionalProductUrl]);
      this.close();
      return;
    }
  }

  private getGhgLocation(path: string[]) {
    if (path[0] === 'project') {
      if (path[1] === ProjectPropName.ghgEmissions) {
        if (path[2] === GhgEmissionsPropName.carbonCredit) {
          this.router.navigateByUrl(path[0] + '/carboncredit');
        } else {
          this.router.navigateByUrl(path[0] + '/carboncredit');
        }
      }
    }
    this.close();
  }

  private hasChildren(obj: Object): boolean {
    return Object.keys(obj) && Object.keys(obj).length > 0;
  }

  private getChildren(obj: Object | Array<Object>, path: string[]): ItemNode[] {
    const result: ItemNode[] = [];
    if (!(obj instanceof ValidationItem) && obj instanceof Object) { // to prevent null of undefined
      for (const item in obj) {
        if (obj.hasOwnProperty(item)) {
          const newPath = path.concat([item]);
          const name = item;
          let valid = true;
          if (this.hasChildren(obj[item]) && !(obj[item] instanceof ValidationItem)) {
            const children = this.getChildren(obj[item], newPath);
            for (const child of children) {
              valid = valid && child.valid;
            }
            result.push({name: name, children: children, valid: valid, path: newPath});
          } else {
            if (!(obj[item] instanceof ValidationItem)) {
              result.push({name: name, valid: false, path: newPath});
            } else {
              result.push({name: name, valid: obj[item].isValid, path: newPath, errMsgs: obj[item].errMsgs[item]});
            }
          }
        }
      }
    } else if (obj instanceof ValidationItem) {
      result.push({name: this.dataIn.section, valid: obj.isValid, path: this.basePath, errMsgs: obj.errMsgs[this.dataIn.section]});
    }
    return result;
  }
}

interface ItemNode {
  name: string;
  children?: ItemNode[];
  valid?: boolean;
  path: string[];
  errMsgs?: string[];
}

interface FlatItemNode {
  expandable: boolean;
  name: string;
  level: number;
  valid: boolean;
  path: string[];
}
